/**
 * Activity Service
 *
 * Handles real-time user activity tracking and subscriptions for admin dashboards:
 * - Real-time presence subscriptions
 * - Activity event subscriptions
 * - Activity statistics queries
 */

import { db } from '../config/firebase';
import {
    collection,
    query,
    where,
    orderBy,
    limit,
    onSnapshot,
    getDocs,
    Timestamp
} from 'firebase/firestore';

/**
 * Convert Firestore timestamp to JavaScript Date
 * @param {Timestamp|Date|Object} timestamp - Firestore timestamp or date-like object
 * @returns {Date|null} JavaScript Date or null if invalid
 */
function convertTimestamp(timestamp) {
    if (!timestamp) return null;
    if (timestamp instanceof Date) return timestamp;
    if (timestamp.toDate && typeof timestamp.toDate === 'function') {
        return timestamp.toDate();
    }
    if (timestamp.seconds) {
        return new Date(timestamp.seconds * 1000);
    }
    return null;
}

/**
 * Subscribe to user presence updates
 * @param {Object} options - Subscription options
 * @param {string} [options.companyId] - Optional company ID to filter by
 * @param {Function} callback - Callback function receiving array of presence objects
 * @returns {Function} Unsubscribe function
 */
export function subscribeToPresence({ companyId } = {}, callback) {
    try {
        const presenceRef = collection(db, 'user_presence');

        // Build query constraints
        // Include 'away' status to prevent flickering when users switch tabs
        const constraints = [
            where('status', 'in', ['online', 'active', 'idle', 'away'])
        ];

        if (companyId) {
            constraints.unshift(where('company_id', '==', companyId));
        }

        const presenceQuery = query(presenceRef, ...constraints);

        const unsubscribe = onSnapshot(
            presenceQuery,
            (snapshot) => {
                const presenceData = snapshot.docs.map(doc => {
                    const data = doc.data();
                    return {
                        id: doc.id,
                        ...data,
                        last_activity: convertTimestamp(data.last_activity),
                        connected_at: convertTimestamp(data.connected_at),
                        updated_at: convertTimestamp(data.updated_at)
                    };
                });

                // Sort by last_activity descending
                presenceData.sort((a, b) => {
                    const timeA = a.last_activity ? a.last_activity.getTime() : 0;
                    const timeB = b.last_activity ? b.last_activity.getTime() : 0;
                    return timeB - timeA;
                });

                callback(presenceData);
            },
            (error) => {
                console.error('[ActivityService] Presence subscription error:', error);
                callback([]);
            }
        );

        return unsubscribe;
    } catch (error) {
        console.error('[ActivityService] Failed to subscribe to presence:', error);
        callback([]);
        return () => {}; // Return no-op unsubscribe
    }
}

/**
 * Subscribe to activity events
 * @param {Object} options - Subscription options
 * @param {string} [options.companyId] - Optional company ID to filter by
 * @param {number} [options.maxEvents=50] - Maximum number of events to return
 * @param {Function} callback - Callback function receiving array of event objects
 * @returns {Function} Unsubscribe function
 */
export function subscribeToEvents({ companyId, maxEvents = 50 } = {}, callback) {
    try {
        const eventsRef = collection(db, 'activity_events');

        // Build query constraints
        const constraints = [
            orderBy('timestamp', 'desc'),
            limit(maxEvents)
        ];

        if (companyId) {
            constraints.unshift(where('company_id', '==', companyId));
        }

        const eventsQuery = query(eventsRef, ...constraints);

        const unsubscribe = onSnapshot(
            eventsQuery,
            (snapshot) => {
                const events = snapshot.docs.map(doc => {
                    const data = doc.data();
                    return {
                        id: doc.id,
                        ...data,
                        timestamp: convertTimestamp(data.timestamp),
                        created_at: convertTimestamp(data.created_at)
                    };
                });

                callback(events);
            },
            (error) => {
                console.error('[ActivityService] Events subscription error:', error);
                callback([]);
            }
        );

        return unsubscribe;
    } catch (error) {
        console.error('[ActivityService] Failed to subscribe to events:', error);
        callback([]);
        return () => {}; // Return no-op unsubscribe
    }
}

/**
 * Get activity statistics
 * @param {Object} options - Query options
 * @param {string} [options.companyId] - Optional company ID to filter by
 * @returns {Promise<Object>} Activity statistics
 */
export async function getActivityStats({ companyId } = {}) {
    try {
        const now = new Date();
        const oneHourAgo = new Date(now.getTime() - 60 * 60 * 1000);
        const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());

        // Query for active users (online or active status)
        const presenceRef = collection(db, 'user_presence');
        const activeConstraints = [
            where('status', 'in', ['online', 'active'])
        ];
        if (companyId) {
            activeConstraints.unshift(where('company_id', '==', companyId));
        }
        const activeQuery = query(presenceRef, ...activeConstraints);
        const activeSnapshot = await getDocs(activeQuery);
        const activeUsers = activeSnapshot.size;

        // Query for events in the last hour
        const eventsRef = collection(db, 'activity_events');
        const hourConstraints = [
            where('timestamp', '>=', Timestamp.fromDate(oneHourAgo))
        ];
        if (companyId) {
            hourConstraints.unshift(where('company_id', '==', companyId));
        }
        const hourQuery = query(eventsRef, ...hourConstraints);
        const hourSnapshot = await getDocs(hourQuery);
        const eventsLastHour = hourSnapshot.size;

        // Query for optimization events today
        const optimizationConstraints = [
            where('timestamp', '>=', Timestamp.fromDate(startOfToday)),
            where('action', 'in', ['started_optimization', 'completed_optimization', 'failed_optimization'])
        ];
        if (companyId) {
            optimizationConstraints.unshift(where('company_id', '==', companyId));
        }
        const optimizationQuery = query(eventsRef, ...optimizationConstraints);
        const optimizationSnapshot = await getDocs(optimizationQuery);
        const optimizationsToday = optimizationSnapshot.size;

        return {
            activeUsers,
            eventsLastHour,
            optimizationsToday
        };
    } catch (error) {
        console.error('[ActivityService] Failed to get activity stats:', error);
        return {
            activeUsers: 0,
            eventsLastHour: 0,
            optimizationsToday: 0
        };
    }
}

/**
 * Format a date as a relative time string
 * @param {Date} date - Date to format
 * @returns {string} Relative time string
 */
export function formatRelativeTime(date) {
    if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
        return '';
    }

    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffSeconds = Math.floor(diffMs / 1000);
    const diffMinutes = Math.floor(diffSeconds / 60);
    const diffHours = Math.floor(diffMinutes / 60);

    if (diffSeconds < 10) {
        return 'Just now';
    }

    if (diffSeconds < 60) {
        return `${diffSeconds}s ago`;
    }

    if (diffMinutes < 60) {
        return `${diffMinutes}m ago`;
    }

    if (diffHours < 24) {
        return `${diffHours}h ago`;
    }

    // For older dates, return formatted date string
    return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit'
    });
}

/**
 * Get human-readable description for an activity event
 * @param {Object} event - Activity event object
 * @returns {string} Human-readable description
 */
export function getActionDescription(event) {
    if (!event || !event.action) {
        return 'performed an action';
    }

    const { action, resource_name, page_name } = event;
    const name = resource_name ? `"${resource_name}"` : '';

    switch (action) {
        // Project actions
        case 'created_project':
            return `created project ${name}`.trim();
        case 'updated_project':
            return `updated project ${name}`.trim();
        case 'deleted_project':
            return `deleted project ${name}`.trim();

        // Scenario actions
        case 'created_scenario':
            return `created scenario ${name}`.trim();
        case 'updated_scenario':
            return `updated scenario ${name}`.trim();
        case 'deleted_scenario':
            return `deleted scenario ${name}`.trim();

        // Optimization actions
        case 'started_optimization':
            return `ran optimization for ${name}`.trim();
        case 'completed_optimization':
            return `completed optimization for ${name}`.trim();
        case 'failed_optimization':
            return `optimization failed for ${name}`.trim();

        // Export actions
        case 'exported_report':
            return `exported report ${name}`.trim();

        // Navigation actions
        case 'viewed_page':
            return `viewed ${page_name || 'a page'}`;

        // Session actions
        case 'session_started':
            return 'started a session';

        // Generic fallback based on action pattern
        default:
            // Try to make action readable (e.g., "created_item" -> "created item")
            const readableAction = action.replace(/_/g, ' ');
            if (name) {
                return `${readableAction} ${name}`.trim();
            }
            return readableAction;
    }
}

/**
 * Get activity history for a specific user
 * @param {string} userId - User ID to get history for
 * @param {number} [maxEvents=20] - Maximum number of events to return
 * @returns {Promise<Object>} Activity history with events and stats
 */
export async function getUserActivityHistory(userId, maxEvents = 20) {
    try {
        if (!userId) {
            return { events: [], stats: { projectsCreated: 0, scenariosCreated: 0, optimizationsRun: 0 } };
        }

        const eventsRef = collection(db, 'activity_events');
        const userEventsQuery = query(
            eventsRef,
            where('user_id', '==', userId),
            orderBy('timestamp', 'desc'),
            limit(maxEvents)
        );

        const snapshot = await getDocs(userEventsQuery);
        const events = snapshot.docs.map(doc => {
            const data = doc.data();
            return {
                id: doc.id,
                ...data,
                timestamp: convertTimestamp(data.timestamp)
            };
        });

        // Calculate stats from events
        const allEventsQuery = query(
            eventsRef,
            where('user_id', '==', userId)
        );
        const allEventsSnapshot = await getDocs(allEventsQuery);

        let projectsCreated = 0;
        let scenariosCreated = 0;
        let optimizationsRun = 0;

        allEventsSnapshot.docs.forEach(doc => {
            const action = doc.data().action;
            if (action === 'created_project') projectsCreated++;
            if (action === 'created_scenario') scenariosCreated++;
            if (action === 'started_optimization' || action === 'completed_optimization') optimizationsRun++;
        });

        return {
            events,
            stats: {
                projectsCreated,
                scenariosCreated,
                optimizationsRun
            }
        };
    } catch (error) {
        console.error('[ActivityService] Failed to get user activity history:', error);
        return { events: [], stats: { projectsCreated: 0, scenariosCreated: 0, optimizationsRun: 0 } };
    }
}

/**
 * Get last activity time for a user from presence or events
 * @param {string} userId - User ID
 * @returns {Promise<Date|null>} Last activity date or null
 */
export async function getUserLastActivity(userId) {
    try {
        if (!userId) return null;

        // First check presence
        const presenceRef = collection(db, 'user_presence');
        const presenceQuery = query(presenceRef, where('__name__', '==', userId), limit(1));
        const presenceSnapshot = await getDocs(presenceQuery);

        if (!presenceSnapshot.empty) {
            const presenceData = presenceSnapshot.docs[0].data();
            if (presenceData.last_activity) {
                return convertTimestamp(presenceData.last_activity);
            }
        }

        // Fall back to most recent event
        const eventsRef = collection(db, 'activity_events');
        const eventsQuery = query(
            eventsRef,
            where('user_id', '==', userId),
            orderBy('timestamp', 'desc'),
            limit(1)
        );
        const eventsSnapshot = await getDocs(eventsQuery);

        if (!eventsSnapshot.empty) {
            const eventData = eventsSnapshot.docs[0].data();
            return convertTimestamp(eventData.timestamp);
        }

        return null;
    } catch (error) {
        console.error('[ActivityService] Failed to get user last activity:', error);
        return null;
    }
}

const activityService = {
    subscribeToPresence,
    subscribeToEvents,
    getActivityStats,
    formatRelativeTime,
    getActionDescription,
    getUserActivityHistory,
    getUserLastActivity
};

export default activityService;
